import { useEffect, useMemo } from 'react';
import {
  Drawer, Stack, TextInput, Select, Switch, Button, Group, Divider, Text,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useForm, useController, type Control, type UseFormSetValue } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTags } from '@/features/tag/useTags';
import { useTranscodeVods } from '@/features/media/useTranscodeVods';
import { TagInput } from '@/shared/components/TagInput';
import { useCreateUploadProfile, useUpdateUploadProfile } from '../../hooks/useUploadProfileMutations';
import {
  uploadProfileSchema,
  UPLOAD_PROFILE_DEFAULTS,
  type UploadProfileFormValues,
} from '../../schemas/uploadProfileSchemas';
import { UPLOAD_PROFILE_CONSTRAINTS } from '@/lib/constants/account';
import type { IngestcfgData, IngestcfgCreateReq } from '@/lib/types/Ingest';

interface UploadProfileDrawerProps {
  opened: boolean;
  profile: IngestcfgData | null;
  onClose: () => void;
  onSuccess: () => void;
}

function profileToFormValues(profile: IngestcfgData): UploadProfileFormValues {
  return {
    label: profile.label,
    transcodeVodId: profile.transcodeVodId,
    tags: [...profile.tags],
    allowAddTags: profile.allowAddTags,
    allowEditDefaultTags: profile.allowEditDefaultTags,
    allowPublish: profile.allowPublish,
  };
}

function formValuesToReq(values: UploadProfileFormValues): IngestcfgCreateReq {
  return {
    label: values.label.trim(),
    transcodeVodId: values.transcodeVodId,
    tags: values.tags,
    allowAddTags: values.allowAddTags,
    allowEditDefaultTags: values.allowEditDefaultTags,
    allowPublish: values.allowPublish,
    playerId: null,
  };
}

export function UploadProfileDrawer({ opened, profile, onClose, onSuccess }: UploadProfileDrawerProps) {
  const isCreate = profile === null;
  const createMutation = useCreateUploadProfile();
  const updateMutation = useUpdateUploadProfile();
  const isPending = isCreate ? createMutation.isPending : updateMutation.isPending;

  const { tags: allTags } = useTags();
  const transcodeVodsQuery = useTranscodeVods();
  const transcodeVods = useMemo(
    () => transcodeVodsQuery.data?.data ?? [],
    [transcodeVodsQuery.data],
  );

  const transcodeVodOptions = useMemo(
    () => [
      { value: '', label: 'No transcoding' },
      ...transcodeVods.map(tv => ({
        value: String(tv.transcodeVodId),
        label: tv.label,
      })),
    ],
    [transcodeVods],
  );

  const form = useForm<UploadProfileFormValues>({
    resolver: zodResolver(uploadProfileSchema),
    defaultValues: UPLOAD_PROFILE_DEFAULTS,
  });

  // Reset form when drawer opens or profile changes
  useEffect(() => {
    if (opened) {
      form.reset(profile ? profileToFormValues(profile) : UPLOAD_PROFILE_DEFAULTS);
    }
  }, [opened, profile, form]);

  async function handleSubmit(values: UploadProfileFormValues) {
    const req = formValuesToReq(values);

    try {
      if (isCreate) {
        await createMutation.mutateAsync(req);
        notifications.show({ title: 'Created', message: `Upload profile "${req.label}" created.`, color: 'green' });
      } else {
        await updateMutation.mutateAsync({ ingestcfgId: profile.ingestcfgId, req });
        notifications.show({ title: 'Saved', message: `Upload profile "${req.label}" updated.`, color: 'green' });
      }
      onSuccess();
    } catch (err) {
      notifications.show({
        title: 'Error',
        message: err instanceof Error ? err.message : `Failed to ${isCreate ? 'create' : 'update'} upload profile.`,
        color: 'red',
      });
    }
  }

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      title={isCreate ? 'Create Upload Profile' : 'Edit Upload Profile'}
      position="right"
      size="md"
      padding="lg"
      closeOnClickOutside={!isPending}
      closeOnEscape={!isPending}
    >
      <form onSubmit={form.handleSubmit(handleSubmit)}>
        <Stack gap="md">
          <LabelField control={form.control} />
          <TranscodeVodField control={form.control} options={transcodeVodOptions} />
          <TagsField control={form.control} allTags={allTags} />

          <Divider label="Advanced Settings" labelPosition="left" mt="sm" />

          <AllowAddTagsField control={form.control} setValue={form.setValue} />
          <AllowEditDefaultTagsField control={form.control} setValue={form.setValue} />
          {/* AllowPublish — commented out, not in use at this time
          <AllowPublishField control={form.control} />
          */}

          <Group justify="flex-end" mt="lg">
            <Button variant="default" onClick={onClose}>Cancel</Button>
            <Button type="submit" loading={isPending}>
              {isCreate ? 'Create Profile' : 'Save Changes'}
            </Button>
          </Group>
        </Stack>
      </form>
    </Drawer>
  );
}

// ── Controlled form fields ──────────────────────────────────────

function LabelField({ control }: { control: Control<UploadProfileFormValues> }) {
  const { field, fieldState } = useController({ control, name: 'label' });

  return (
    <TextInput
      label="Label"
      withAsterisk
      maxLength={UPLOAD_PROFILE_CONSTRAINTS.LABEL_MAX}
      description={`${field.value.length}/${UPLOAD_PROFILE_CONSTRAINTS.LABEL_MAX}`}
      error={fieldState.error?.message}
      value={field.value}
      onChange={field.onChange}
      onBlur={field.onBlur}
    />
  );
}

function TranscodeVodField({
  control,
  options,
}: {
  control: Control<UploadProfileFormValues>;
  options: { value: string; label: string }[];
}) {
  const { field } = useController({ control, name: 'transcodeVodId' });

  return (
    <Select
      label="Transcode Profile"
      data={options}
      value={field.value !== null ? String(field.value) : ''}
      onChange={(val) => {
        field.onChange(val && val !== '' ? Number(val) : null);
      }}
      onBlur={field.onBlur}
      clearable={false}
    />
  );
}

function TagsField({
  control,
  allTags,
}: {
  control: Control<UploadProfileFormValues>;
  allTags: string[];
}) {
  const { field, fieldState } = useController({ control, name: 'tags' });

  return (
    <TagInput
      label="Tags"
      description={field.value.length > 0
        ? 'Default tags applied to uploads using this profile'
        : 'No default tags — uploads will have no tags unless added by the uploader'}
      value={field.value}
      onChange={field.onChange}
      onBlur={field.onBlur}
      allTags={allTags}
      allowNewTags
      placeholder="Add default tags..."
      error={fieldState.error?.message}
    />
  );
}

function AllowAddTagsField({
  control,
  setValue,
}: {
  control: Control<UploadProfileFormValues>;
  setValue: UseFormSetValue<UploadProfileFormValues>;
}) {
  const { field } = useController({ control, name: 'allowAddTags' });

  return (
    <Switch
      label="Allow Add Tags"
      description="Uploaders can add tags beyond the defaults"
      checked={field.value}
      onChange={(e) => {
        const checked = e.currentTarget.checked;
        field.onChange(checked);
        if (checked) setValue('allowEditDefaultTags', false, { shouldDirty: true });
      }}
    />
  );
}

function AllowEditDefaultTagsField({
  control,
  setValue,
}: {
  control: Control<UploadProfileFormValues>;
  setValue: UseFormSetValue<UploadProfileFormValues>;
}) {
  const { field } = useController({ control, name: 'allowEditDefaultTags' });

  return (
    <Switch
      label="Allow Edit Default Tags"
      description="Uploaders can add or remove default tags from uploads"
      checked={field.value}
      onChange={(e) => {
        const checked = e.currentTarget.checked;
        field.onChange(checked);
        if (checked) setValue('allowAddTags', false, { shouldDirty: true });
      }}
    />
  );
}

/* AllowPublish — commented out, not in use at this time
function AllowPublishField({ control }: { control: Control<UploadProfileFormValues> }) {
  const { field } = useController({ control, name: 'allowPublish' });

  return (
    <Switch
      label="Allow Publish"
      description="Uploaders can set the active state of uploads"
      checked={field.value}
      onChange={(e) => field.onChange(e.currentTarget.checked)}
    />
  );
}
*/
